import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Brand from "@/components/Brand";
import PatchNotesModal from "@/components/PatchNotesModal";
import BadgeAccessories from "@/components/BadgeAccessories";
import StoreTab from "@/components/StoreTab";
import MusicTab from "@/components/MusicTab";
import TosTab from "@/components/TosTab";
import SettingsTab from "@/components/SettingsTab";
import AchievementsTab from "@/components/AchievementsTab";
import PurchaseHistory from "@/components/PurchaseHistory";
import ReferralTab from "@/components/ReferralTab";
import SupportUsTab from "@/components/SupportUsTab";
import { FLAGS } from "@/lib/flags";

const FLAGS_OPTIONS = FLAGS;
const FLAGS_LOOKUP = Object.fromEntries(FLAGS.map(f => [f.code, f]));

const TABS = [
  { key: "profile",  label: "Profile",      icon: "fa-user" },
  { key: "billing",  label: "Billing",      icon: "fa-credit-card" },
  { key: "plan",     label: "Subscription", icon: "fa-crown" },
  { key: "store",    label: "Store",        icon: "fa-bag-shopping" },
  { key: "music",    label: "Music",        icon: "fa-music" },
  { key: "settings", label: "Settings",     icon: "fa-sliders" },
  { key: "patch",    label: "Patch Notes",  icon: "fa-clipboard-list" },
  { key: "privacy",  label: "Privacy",      icon: "fa-user-shield" },
  { key: "friends",  label: "Friends",      icon: "fa-users" },
  { key: "supportus", label: "Support Us",  icon: "fa-heart" },
  { key: "achievements", label: "Achievements", icon: "fa-trophy" },
  { key: "security", label: "Security",     icon: "fa-shield" },
  { key: "tos",      label: "Terms",        icon: "fa-file-contract" },
];

const TAB_KEYS = TABS.map(t => t.key);

export default function AccountPage() {
  const { user, refreshMe, logout } = useAuth();
  const location = useLocation();
  const initialTab = (() => {
    const q = new URLSearchParams(location.search).get("tab");
    return ["profile","billing","plan","store","music","settings","patch","privacy","friends","supportus","achievements","security","tos"].includes(q) ? q : "profile";
  })();
  const [tab, setTab] = useState(initialTab);
  const [showPatch, setShowPatch] = useState(false);
  useEffect(() => {
    if (tab === "patch") { setShowPatch(true); setTab("profile"); }
  }, [tab]);
  const [friends, setFriends] = useState({ following: [], followers: [], hidden: false });
  useEffect(() => {
    if (!user) return;
    (async () => {
      try { const { data } = await api.get(`/social/friends/${user.id}`); setFriends(data); } catch {}
    })();
  }, [user]);
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio]   = useState(user?.bio  || "");
  const [country, setCountry] = useState(user?.country || "");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  // Billing
  const [billing, setBilling] = useState({ tier: "free", card: null, tier_locked: false });
  const [tiers, setTiers] = useState({});
  const [card, setCard] = useState({ cardholder: "", number: "", expiry: "", cvc: "", billing_zip: "" });

  // Privacy
  const [privacy, setPrivacy] = useState({ profile_public: true, allow_messages: true });
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/social/privacy"); setPrivacy(data); } catch {}
    })();
  }, []);
  async function savePrivacy(patch) {
    const next = { ...privacy, ...patch };
    setPrivacy(next);
    try { await api.patch("/social/privacy", patch); flash("Privacy updated."); }
    catch (e) { fail(e); }
  }

  // Security
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });

  // Sync local form state when /me payload changes (e.g. after a save)
  useEffect(() => {
    if (user?.name !== undefined) setName(user.name || "");
    if (user?.bio  !== undefined) setBio(user.bio || "");
    if (user?.country !== undefined) setCountry(user.country || "");
  }, [user?.name, user?.bio, user?.country]);

  useEffect(() => {
    (async () => {
      try {
        const [b, t] = await Promise.all([api.get("/billing"), api.get("/billing/tiers")]);
        setBilling(b.data); setTiers(t.data);
      } catch {}
    })();
  }, []);

  const flash = (m) => { setMsg(m); setErr(""); setTimeout(() => setMsg(""), 2200); };
  const fail  = (e) => { setErr(fmtErr(e.response?.data?.detail) || e.message); setMsg(""); };

  async function saveName() {
    try { await api.patch("/auth/me", { name }); await refreshMe(); flash("Profile updated."); }
    catch (e) { fail(e); }
  }
  async function saveBio() {
    try { await api.patch("/auth/me", { bio }); await refreshMe(); flash("Bio saved."); }
    catch (e) { fail(e); }
  }
  async function uploadAvatar(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { fail({ response: { data: { detail: "Pick an image file" } } }); return; }
    if (file.size > 200_000) { fail({ response: { data: { detail: "Image too large — keep under 200 KB" } } }); return; }
    setUploadingAvatar(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.patch("/auth/me", { avatar: reader.result });
          await refreshMe();
          flash("Profile photo updated.");
        } catch (e2) { fail(e2); }
        finally { setUploadingAvatar(false); }
      };
      reader.readAsDataURL(file);
    } catch (e3) { fail(e3); setUploadingAvatar(false); }
  }
  async function removeAvatar() {
    try { await api.patch("/auth/me", { avatar: "" }); await refreshMe(); flash("Profile photo removed."); }
    catch (e) { fail(e); }
  }
  async function saveCard(e) {
    e.preventDefault();
    try {
      const { data } = await api.post("/billing/card", card);
      setBilling(b => ({ ...b, card: data.card }));
      setCard({ cardholder: "", number: "", expiry: "", cvc: "", billing_zip: "" });
      flash(`${data.card.brand} •••• ${data.card.last4} saved.`);
    } catch (e2) { fail(e2); }
  }
  async function removeCard() {
    try { await api.delete("/billing/card"); setBilling(b => ({ ...b, card: null })); flash("Card removed."); }
    catch (e) { fail(e); }
  }
  async function setTier(t) {
    if (t === "free") {
      if (!window.confirm("Cancel your subscription and drop back to Free tier?")) return;
      try { await api.post("/billing/cancel", { reason: "user_initiated" }); setBilling(b => ({ ...b, tier: "free" })); await refreshMe(); flash("Subscription cancelled."); }
      catch (e) { fail(e); }
      return;
    }
    // Pro / Elite — open Stripe checkout
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: t,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { fail(e); }
  }

  // Stripe return — poll the session for payment status
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const cancelled = params.get("cancelled");
    if (cancelled) {
      setErr("Checkout cancelled.");
      return;
    }
    if (!sid) return;
    let cancel = false;
    (async () => {
      flash("Verifying payment…");
      for (let i = 0; i < 8 && !cancel; i++) {
        try {
          const { data } = await api.get(`/payments/status/${sid}`);
          if (data.payment_status === "paid") {
            const { data: b } = await api.get("/billing");
            setBilling(b);
            flash(`Welcome to ${data.tier?.toUpperCase()}! 🎉`);
            // Strip session_id from URL so refresh doesn't re-poll
            window.history.replaceState({}, "", "/account?tab=plan");
            return;
          }
          if (data.status === "expired") { setErr("Checkout session expired."); return; }
        } catch {}
        await new Promise(r => setTimeout(r, 2000));
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function changePwd(e) {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      flash("Password updated.");
    } catch (e2) { fail(e2); }
  }

  return (
    <>
    <div className="min-h-screen bg-[#0b0e14] text-slate-200 font-mono" data-testid="account-page">
      <Header user={user} logout={logout} />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-white">Account</h1>
          <Link to="/app" data-testid="back-to-app" className="text-xs text-blue-400 hover:underline">← Back to Sandbox</Link>
        </div>
        <div className="grid md:grid-cols-[200px_1fr] gap-6">
          <nav className="space-y-1">
            {TABS.map(t => (
              <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all ${tab === t.key ? "bg-blue-600/20 text-blue-300 border border-blue-500/30" : "text-slate-400 hover:bg-slate-800/60 border border-transparent"}`}>
                <i className={`fa-solid ${t.icon} mr-1.5`}/> {t.label}
              </button>
            ))}
          </nav>
          <div className="bg-[#11151d] border border-slate-800 rounded-2xl p-6">
            {msg && <div data-testid="account-msg" className="mb-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-3 py-2 rounded">{msg}</div>}
            {err && <div data-testid="account-err" className="mb-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 px-3 py-2 rounded">{err}</div>}

            {tab === "profile" && (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {user?.avatar ? (
                      <img data-testid="avatar-preview" src={user.avatar} alt="avatar" className="w-20 h-20 rounded-full object-cover border-2 border-slate-700"/>
                    ) : (
                      <div data-testid="avatar-placeholder" className="w-20 h-20 rounded-full bg-slate-800 border-2 border-slate-700 flex items-center justify-center text-2xl font-black text-slate-500">
                        {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <label className="block">
                      <input data-testid="avatar-upload" type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={uploadAvatar} disabled={uploadingAvatar} className="hidden"/>
                      <span className="cursor-pointer inline-block px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold text-white">
                        {uploadingAvatar ? <><i className="fa-solid fa-spinner fa-spin mr-1"/> Uploading…</> : <><i className="fa-solid fa-upload mr-1"/> Change photo</>}
                      </span>
                    </label>
                    {user?.avatar && (
                      <button data-testid="avatar-remove" onClick={removeAvatar} className="block text-[11px] text-rose-300 hover:text-rose-200">Remove photo</button>
                    )}
                    <div className="text-[10px] text-slate-500">PNG / JPG / WebP under 200 KB</div>
                  </div>
                </div>
                <Field label="Email (read-only)"><div className="text-slate-400 text-sm">{user?.email}</div></Field>
                <Field label="Role"><div className="text-amber-400 text-sm font-bold uppercase">{user?.role}</div></Field>
                <Field label="Status"><StatusPill status={user?.status || "active"}/></Field>
                <Field label="Display name">
                  <div className="flex gap-2">
                    <input data-testid="name-input" value={name} onChange={e => setName(e.target.value)} className="flex-1 px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500"/>
                    <button data-testid="save-name" onClick={saveName} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold">Save</button>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">5 free renames · then $0.99 per rename (Store → Rename Item)</div>
                </Field>
                <Field label="Bio (max 30 words)">
                  <div className="flex gap-2">
                    <textarea data-testid="bio-input" value={bio} onChange={e => setBio(e.target.value)} rows={2} maxLength={300}
                      placeholder="A short blurb about your trading style…"
                      className="flex-1 px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 resize-none"/>
                    <button data-testid="save-bio" onClick={saveBio} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold self-start">Save</button>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{bio.trim() ? bio.trim().split(/\s+/).length : 0}/30 words</div>
                </Field>
                <Field label="National Flag">
                  <div className="flex gap-2 items-center">
                    <span className="text-xl w-7 text-center">{(FLAGS_LOOKUP[country] || {}).flag || "🏳️"}</span>
                    <select data-testid="profile-country" value={country} onChange={e => setCountry(e.target.value)}
                      className="flex-1 px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 cursor-pointer">
                      <option value="">— No flag —</option>
                      {FLAGS_OPTIONS.map(f => <option key={f.code} value={f.code}>{f.flag}  {f.name}</option>)}
                    </select>
                    <button data-testid="save-country" onClick={async () => {
                      try { await api.patch("/auth/me", { country }); await refreshMe(); flash("Flag updated."); }
                      catch (e) { fail(e); }
                    }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold">Save</button>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Shown next to your name in chat, leaderboards, and your profile header.</div>
                </Field>
                <div className="grid grid-cols-2 gap-3 pt-2">
                  <Stat label="Balance" value={`$${(user?.balance || 0).toFixed(2)}`}/>
                  <Stat label="Member since" value={(user?.created_at || "").slice(0,10)}/>
                </div>
                <BadgeAccessories />
              </div>
            )}

            {tab === "billing" && (
              <div className="space-y-4">
                {billing.card ? (
                  <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-xl p-5 text-white">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="text-[10px] text-slate-400 uppercase">Card on file</div>
                        <div className="text-2xl font-bold mt-1 tracking-wider">•••• •••• •••• {billing.card.last4}</div>
                        <div className="text-xs text-slate-400 mt-2">{billing.card.cardholder} · exp {billing.card.expiry}</div>
                      </div>
                      <div className="text-amber-300 font-bold">{billing.card.brand}</div>
                    </div>
                    <button data-testid="remove-card" onClick={removeCard} className="mt-3 text-[11px] text-rose-300 hover:text-rose-200">Remove card</button>
                  </div>
                ) : (
                  <form onSubmit={saveCard} className="space-y-3" data-testid="card-form">
                    <div className="text-xs text-slate-500"><i className="fa-solid fa-lock mr-1"/> Card info is masked & stored locally only.</div>
                    <Field label="Cardholder">
                      <input data-testid="card-cardholder" required value={card.cardholder} onChange={e => setCard({...card, cardholder: e.target.value})} className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
                    </Field>
                    <Field label="Card number">
                      <input data-testid="card-number" required value={card.number} onChange={e => setCard({...card, number: e.target.value})} placeholder="4242 4242 4242 4242" className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/>
                    </Field>
                    <div className="grid grid-cols-3 gap-2">
                      <Field label="Expiry"><input data-testid="card-expiry" required value={card.expiry} onChange={e => setCard({...card, expiry: e.target.value})} placeholder="MM/YY" className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/></Field>
                      <Field label="CVC"><input data-testid="card-cvc" required value={card.cvc} onChange={e => setCard({...card, cvc: e.target.value})} placeholder="123" className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/></Field>
                      <Field label="ZIP"><input data-testid="card-zip" value={card.billing_zip} onChange={e => setCard({...card, billing_zip: e.target.value})} className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/></Field>
                    </div>
                    <button data-testid="save-card" type="submit" className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold">Save Card</button>
                  </form>
                )}
                <PurchaseHistory />
              </div>
            )}

            {tab === "plan" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-500">Current plan: <span className="text-blue-300 font-bold">{billing.tier?.toUpperCase()}</span></div>
                  {billing.tier_locked && (
                    <div data-testid="tier-locked-badge" className="text-[10px] font-bold uppercase px-2 py-1 rounded bg-amber-500/10 border border-amber-500/30 text-amber-300">
                      <i className="fa-solid fa-lock mr-1"/> Tier locked by admin
                    </div>
                  )}
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {Object.entries(tiers).map(([key, t]) => {
                    const isCurrent = billing.tier === key;
                    const disabled = isCurrent || billing.tier_locked;
                    const isDowngrade = key === "free" && billing.tier !== "free";
                    const isUpgrade   = key !== "free" && !isCurrent;
                    let label;
                    if (isCurrent) label = "Current Plan";
                    else if (billing.tier_locked) label = "Locked";
                    else if (isDowngrade) label = "Cancel Subscription";
                    else if (isUpgrade)   label = `Upgrade to ${t.name}`;
                    else label = `Switch to ${t.name}`;
                    return (
                      <div key={key} className={`rounded-xl border p-4 ${isCurrent ? "border-blue-500/50 bg-blue-500/5" : "border-slate-800 bg-slate-900/40"}`}>
                        <div className="text-amber-400 text-xs font-bold uppercase">{t.name}</div>
                        <div className="text-3xl font-black text-white mt-1">${t.price}<span className="text-xs text-slate-500">/mo</span></div>
                        <ul className="mt-3 space-y-1 text-[11px] text-slate-300">
                          {t.features.map(f => <li key={f}><i className="fa-solid fa-check text-emerald-400 mr-1.5"/>{f}</li>)}
                        </ul>
                        <button data-testid={`tier-${key}`} disabled={disabled} onClick={() => setTier(key)}
                          className={`w-full mt-4 py-2 rounded-lg text-xs font-bold ${
                            disabled ? "bg-slate-800 text-slate-500 cursor-not-allowed"
                            : isDowngrade ? "bg-rose-600/80 hover:bg-rose-600 text-white"
                            : isUpgrade ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-slate-900"
                            : "bg-blue-600 hover:bg-blue-700 text-white"
                          }`}>
                          {label}
                        </button>
                      </div>
                    );
                  })}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">
                  <i className="fa-solid fa-lock mr-1"/> Cancellation is one-click.
                </div>
              </div>
            )}

            {tab === "store" && <StoreTab />}
            {tab === "music" && <MusicTab />}
            {tab === "tos"   && <TosTab />}

            {tab === "settings" && <SettingsTab />}

            {tab === "privacy" && (
              <div className="space-y-4 max-w-2xl" data-testid="privacy-tab">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
                  <i className="fa-solid fa-user-shield mr-1.5 text-blue-400"/> Profile & Social
                </div>
                <label className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
                  <div>
                    <div className="text-sm text-white font-bold">Profile visibility</div>
                    <div className="text-[10px] text-slate-500">Public = anyone can see your stats and trade summary. Private = only your name shows.</div>
                  </div>
                  <select data-testid="toggle-profile-visibility" value={privacy.profile_public ? "public" : "private"}
                    onChange={e => savePrivacy({ profile_public: e.target.value === "public" })}
                    className="bg-[#161d28] border border-slate-700 rounded px-2 py-1 text-xs font-bold uppercase cursor-pointer">
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </label>
                <label className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
                  <div>
                    <div className="text-sm text-white font-bold">Accept direct messages</div>
                    <div className="text-[10px] text-slate-500">Off = the "Message" button disappears on your public profile.</div>
                  </div>
                  <input data-testid="toggle-allow-messages" type="checkbox" checked={privacy.allow_messages}
                    onChange={e => savePrivacy({ allow_messages: e.target.checked })}
                    className="w-5 h-5 accent-blue-400 cursor-pointer"/>
                </label>
                <label className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
                  <div>
                    <div className="text-sm text-white font-bold">Friends list visibility</div>
                    <div className="text-[10px] text-slate-500">Private hides your followers/following from other users.</div>
                  </div>
                  <select data-testid="toggle-friends-list-public" value={privacy.friends_list_public ? "public" : "private"}
                    onChange={e => savePrivacy({ friends_list_public: e.target.value === "public" })}
                    className="bg-[#161d28] border border-slate-700 rounded px-2 py-1 text-xs font-bold uppercase cursor-pointer">
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </label>
                <label className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
                  <div>
                    <div className="text-sm text-white font-bold">Friends-Only profile</div>
                    <div className="text-[10px] text-slate-500">Only people you follow can see your full profile + stats.</div>
                  </div>
                  <input data-testid="toggle-friends-only" type="checkbox" checked={!!privacy.friends_only_profile}
                    onChange={e => savePrivacy({ friends_only_profile: e.target.checked })}
                    className="w-5 h-5 accent-blue-400 cursor-pointer"/>
                </label>
                <label className="flex items-center justify-between bg-slate-900/60 border border-slate-800 rounded-lg px-4 py-3 cursor-pointer">
                  <div>
                    <div className="text-sm text-white font-bold">Title Display</div>
                    <div className="text-[10px] text-slate-500">Off = hide your earned rank title (Novice/Developing/etc.) next to your name everywhere.</div>
                  </div>
                  <input data-testid="toggle-title-display" type="checkbox" checked={!privacy.title_display_off}
                    onChange={e => savePrivacy({ title_display_off: !e.target.checked })}
                    className="w-5 h-5 accent-amber-400 cursor-pointer"/>
                </label>
              </div>
            )}

            {tab === "friends" && (
              <div className="space-y-4" data-testid="friends-tab">
                <div className="grid md:grid-cols-2 gap-4">
                  <FriendsList title="Following" people={friends.following} testidPrefix="following"/>
                  <FriendsList title="Followers" people={friends.followers} testidPrefix="follower"/>
                </div>
                <div className="text-[10px] text-slate-600">Open a leaderboard row or any public profile to follow / block / report a trader.</div>
                <div className="pt-4 border-t border-slate-800">
                  <ReferralTab />
                </div>
              </div>
            )}

            {tab === "supportus" && <SupportUsTab />}

            {tab === "achievements" && <AchievementsTab />}

            {tab === "security" && (
              <div className="space-y-6 max-w-md">
                <form onSubmit={changePwd} className="space-y-3" data-testid="pwd-form">
                  <Field label="Current password"><input data-testid="pwd-current" type="password" required value={pwd.current_password} onChange={e => setPwd({...pwd, current_password: e.target.value})} className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/></Field>
                  <Field label="New password (min 6 chars)"><input data-testid="pwd-new" type="password" required minLength={6} value={pwd.new_password} onChange={e => setPwd({...pwd, new_password: e.target.value})} className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm"/></Field>
                  <button data-testid="pwd-save" type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-xs font-bold">Update password</button>
                </form>

                <div className="pt-4 border-t border-slate-800" data-testid="support-section">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">
                    <i className="fa-solid fa-life-ring mr-1.5 text-blue-400"/> Support
                  </div>
                  <p className="text-xs text-slate-400 mb-3">
                    Need help with your account, a billing question, or hit a bug? Our team usually replies within 24 hours.
                  </p>
                  <a
                    data-testid="contact-support-btn"
                    href={`mailto:institutionalsandboxsupport@gmail.com?subject=${encodeURIComponent("Institutional Sandbox Support")}&body=${encodeURIComponent(`Hi support team,\n\nAccount: ${user?.email || ""}\n\nDescribe your issue here:\n`)}`}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/30 text-blue-300 rounded-lg text-xs font-bold transition-all">
                    <i className="fa-solid fa-envelope"/> Contact Support
                  </a>
                  <div className="text-[10px] text-slate-600 mt-2">institutionalsandboxsupport@gmail.com</div>
                </div>

                <DeleteAccountPanel onDeleted={logout} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    {showPatch && <PatchNotesModal onClose={() => setShowPatch(false)} />}
    </>
  );
}

function Field({ label, children }) {
  return <div><div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1">{label}</div>{children}</div>;
}
function Stat({ label, value }) {
  return <div className="bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2"><div className="text-[10px] text-slate-500 uppercase">{label}</div><div className="text-sm text-white font-bold">{value}</div></div>;
}
function StatusPill({ status }) {
  const map = { active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
                restricted: "bg-amber-500/20 text-amber-300 border-amber-500/30",
                banned: "bg-rose-500/20 text-rose-300 border-rose-500/30" };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${map[status] || map.active}`}>{status}</span>;
}

function Header({ user, logout }) {
  return (
    <header className="bg-[#11151d] border-b border-slate-800 px-6 py-3 flex justify-between items-center">
      <Link to="/" className="flex items-center gap-2">
        <Brand compact />
      </Link>
      <div className="flex items-center gap-3 text-xs">
        <Link to="/app" className="text-slate-400 hover:text-white">Sandbox</Link>
        <Link to="/account" className="text-blue-300 font-bold">Account</Link>
        {user?.role === "admin" && <Link to="/admin" data-testid="nav-admin" className="text-amber-400 hover:text-amber-300 font-bold">Admin</Link>}
        <button onClick={logout} className="text-slate-500 hover:text-rose-400"><i className="fa-solid fa-right-from-bracket"/></button>
      </div>
    </header>
  );
}

function FriendsList({ title, people, testidPrefix }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">
        <i className="fa-solid fa-user-group mr-1"/> {title} ({people?.length || 0})
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {(!people || people.length === 0) && <div className="text-slate-600 text-xs">Nobody here yet.</div>}
        {people && people.map(p => (
          <Link key={p.id} to={`/u/${p.id}`} data-testid={`${testidPrefix}-${p.id}`}
            className="flex items-center gap-2 hover:bg-slate-800/40 rounded px-2 py-1.5 transition-colors">
            {p.avatar
              ? <img src={p.avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-slate-700"/>
              : <div className="w-6 h-6 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[9px] text-slate-500">{(p.name||"?").charAt(0).toUpperCase()}</div>}
            <span className="text-xs text-white">{p.name}</span>
            <span className={`w-1.5 h-1.5 rounded-full ${p.online ? "bg-emerald-400" : "bg-slate-600"} ml-auto`}/>
          </Link>
        ))}
      </div>
    </div>
  );
}



function DeleteAccountPanel({ onDeleted }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function doDelete() {
    setBusy(true); setErr("");
    try {
      await api.delete("/auth/me", { data: { password: pwd, confirm: true } });
      alert("Your account has been permanently deleted.");
      onDeleted && onDeleted();
      window.location.href = "/";
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  }

  return (
    <div className="pt-4 border-t border-rose-900/40" data-testid="delete-account-section">
      <div className="text-[10px] text-rose-400 uppercase tracking-wider font-bold mb-2">
        <i className="fa-solid fa-triangle-exclamation mr-1.5"/> Danger Zone
      </div>
      {!open ? (
        <button data-testid="delete-account-btn" onClick={() => setOpen(true)}
          className="px-4 py-2 bg-rose-600/15 hover:bg-rose-600/25 border border-rose-500/40 text-rose-300 rounded-lg text-xs font-bold">
          <i className="fa-solid fa-trash mr-1.5"/> Delete Account
        </button>
      ) : (
        <div className="bg-rose-950/30 border border-rose-500/30 rounded-lg p-3 space-y-3" data-testid="delete-account-confirm">
          <p className="text-xs text-rose-200">
            Proceeding will permanently delete your account and all associated items. Check the box and click <b>'Delete'</b> to confirm.
          </p>
          <input data-testid="delete-pwd" type="password" placeholder="Confirm with your password" value={pwd}
            onChange={e => setPwd(e.target.value)}
            className="w-full px-3 py-2 bg-[#161d28] border border-rose-500/30 rounded-lg text-sm"/>
          <label className="flex items-center gap-2 text-xs text-rose-200 cursor-pointer">
            <input data-testid="delete-confirm-check" type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)}
              className="w-4 h-4 accent-rose-500"/>
            I understand this is permanent and irreversible.
          </label>
          {err && <div className="text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">{err}</div>}
          <div className="flex gap-2">
            <button data-testid="delete-account-final" disabled={!confirm || !pwd || busy} onClick={doDelete}
              className="px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 rounded-lg text-xs font-bold text-white">
              {busy ? "Deleting…" : "Delete"}
            </button>
            <button onClick={() => { setOpen(false); setPwd(""); setConfirm(false); setErr(""); }}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-xs font-bold">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
